import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RefreshCw, AlertTriangle, Crown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useCustomPageTitle } from '../../hooks/usePageTitle';
import { deleteUserAccount, updateUserProfile } from '../../services/profileService';
import PersonalInfo from '../../components/profile/PersonalInfo';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// ─── Design tokens ─────────────────────────────────────────────────────────────

const CARD: React.CSSProperties = {
  background: 'white',
  border: '0.5px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  padding: '20px 24px',
};

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: '#7C6FA0',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 16,
};

// ─── Component ─────────────────────────────────────────────────────────────────

const Profile = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { profile, loading, error, reloadCredits } = useUserProfile(user?.uid);
  useCustomPageTitle('Mon profil');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (location.state?.forceReload) {
      setTimeout(() => reloadCredits?.(), 500);
    }
  }, [location.state, reloadCredits]);

  const handleUpdateProfile = async (field: string, value: string) => {
    if (!user || !profile) return;
    await updateUserProfile(user.uid, { [field]: value });
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    try {
      await deleteUserAccount(user.uid);
    } catch {
      setDeleteError('Une erreur est survenue lors de la suppression du compte');
    }
  };

  if (loading) return <div className="p-8"><LoadingSpinner /></div>;
  if (error) return <div className="p-8"><ErrorMessage message={error} /></div>;

  const credits = profile?.credits ?? 0;
  const isUnlimited = profile?.subscriptionStatus === 'active' || profile?.subscriptionStatus === 'trialing';

  return (
    <div className="px-6 py-6 flex flex-col gap-4" style={{ background: 'var(--color-surface)', minHeight: '100%' }}>

      {/* ── Informations personnelles ── */}
      <div style={CARD}>
        <p style={SECTION_LABEL}>Informations personnelles</p>
        <PersonalInfo
          profile={profile || {}}
          onUpdate={handleUpdateProfile}
        />
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '0.5px solid var(--color-border)' }}>
          <p style={{ fontSize: 11, color: '#7C6FA0', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 3 }}>
            Email
          </p>
          <p style={{ fontSize: 14, color: '#1E1148' }}>{user?.email}</p>
        </div>
      </div>

      {/* ── Mon solde de crédits ── */}
      <div style={CARD}>
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <p style={SECTION_LABEL}>Mon solde de crédits</p>
          {!isUnlimited && reloadCredits && (
            <button
              onClick={() => reloadCredits()}
              style={{ color: '#7C6FA0', background: 'transparent', border: 'none', cursor: 'pointer', padding: 2 }}
              title="Actualiser"
            >
              <RefreshCw size={13} strokeWidth={1.75} />
            </button>
          )}
        </div>

        {isUnlimited ? (
          <div className="flex items-center gap-3">
            <Crown size={22} color="#F97316" strokeWidth={1.75} style={{ flexShrink: 0 }} />
            <div className="flex-1">
              <p style={{ fontSize: 14, fontWeight: 500, color: '#1E1148' }}>Plan Professionnel</p>
              <p style={{ fontSize: 12, color: '#7C6FA0', marginTop: 2 }}>Accès illimité</p>
            </div>
            <Link
              to="/billing"
              style={{ fontSize: 12, color: 'rgb(var(--color-primary))', textDecoration: 'none', flexShrink: 0 }}
            >
              Gérer l'abonnement →
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-6">
            {/* Big number */}
            <div>
              <p style={{ fontSize: 42, fontWeight: 700, lineHeight: 1, color: 'rgb(var(--color-primary))' }}>
                {credits}
              </p>
              <p style={{ fontSize: 12, color: '#7C6FA0', marginTop: 4 }}>
                {`estimation${credits !== 1 ? 's' : ''} disponible${credits !== 1 ? 's' : ''}`}
              </p>
            </div>

            {/* Divider */}
            <div style={{ width: '0.5px', alignSelf: 'stretch', background: 'var(--color-border)' }} />

            {/* Info + actions */}
            <div className="flex-1">
              <p style={{ fontSize: 12, color: '#7C6FA0', lineHeight: 1.5 }}>
                Chaque crédit permet d'estimer une adresse différente. Les crédits n'expirent jamais.
              </p>
              <div className="flex items-center gap-4 mt-3">
                <Link
                  to="/credits/purchase"
                  className="btn btn-primary"
                  style={{ fontSize: 12, padding: '5px 12px' }}
                >
                  + Recharger
                </Link>
                <Link
                  to="/billing"
                  style={{ fontSize: 12, color: 'rgb(var(--color-primary))', textDecoration: 'none' }}
                >
                  Gérer l'abonnement →
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Zone dangereuse ── */}
      <div style={{
        ...CARD,
        border: '0.5px solid rgba(220,38,38,0.2)',
      }}>
        <p style={{ ...SECTION_LABEL, color: '#DC2626' }}>Zone dangereuse</p>
        <p style={{ fontSize: 13, color: '#7C6FA0', marginBottom: 14 }}>
          La suppression de votre compte est irréversible. Toutes vos données et estimations seront définitivement perdues.
        </p>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          style={{
            fontSize: 12,
            padding: '5px 14px',
            border: '1px solid rgba(220,38,38,0.4)',
            borderRadius: 'var(--radius-sm)',
            color: '#DC2626',
            background: 'transparent',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <AlertTriangle size={13} strokeWidth={1.75} />
          Supprimer mon compte
        </button>
        {deleteError && (
          <p style={{ marginTop: 8, fontSize: 12, color: '#DC2626' }}>{deleteError}</p>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Supprimer votre compte ?"
        message="Cette action est irréversible. Toutes vos données, y compris vos estimations et crédits, seront définitivement supprimées. Êtes-vous sûr de vouloir continuer ?"
        confirmLabel="Supprimer définitivement"
        cancelLabel="Annuler"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />
    </div>
  );
};

export default Profile;
